import { useEffect, useMemo, useState } from 'react'

import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { throttle } from '@src/utils/functions/utils'

import { useShouldHideDeptAndCategoryFilter, filterListOnExp } from '../../filterListOnExp'
import { Filter } from '../filterSection.interface'
import { excludeDepartmentFilter } from '../filterUtils'

const FILTER_KEY_GLOBAL = 'global'

interface MobileFiltersProps {
  serverFilters: Filter[]
  collectionSlug?: string
}

interface MobileFiltersReturn {
  isDrawerOpen: boolean
  isSortDrawerOpen: boolean
  collapseActiveKeys: string[]
  headerHeight: number
  isScrollingDown: boolean
  filters: Filter[]
  openDrawerWithFilter: (filterName: string) => void
  openSortDrawer: () => void
  closeDrawer: () => void
  closeSortDrawer: () => void
  handleCollapseChange: (activeKeys: string[]) => void
  handleMainFilterClick: () => void
}

const useMobileFilters = ({ serverFilters, collectionSlug }: MobileFiltersProps): MobileFiltersReturn => {
  // Move all hooks before any conditional returns
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false)
  const [collapseActiveKeys, setCollapseActiveKeys] = useState<string[]>([])
  const [headerHeight, setHeaderHeight] = useState(0)

  // Track if the header is visible (user is scrolling up)
  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  // Get header height when component mounts and when scrolling direction changes
  useEffect(() => {
    if (isBrowser()) {
      const getHeaderHeight = throttle(() => {
        const headerElement = document.getElementById('site-primary-header')
        const subCollectionCardListElement = document.getElementById('subcollection-card-list')
        const subCategoryListElement = document.getElementById('sub-category-list-header')
        const headerHeight = headerElement?.getBoundingClientRect().height
        const subCollectionCardListHeight = subCollectionCardListElement?.getBoundingClientRect().height
        const subCategoryListHeight = subCategoryListElement?.getBoundingClientRect().height

        setHeaderHeight((headerHeight ?? 0) + (subCollectionCardListHeight ?? 0) + (subCategoryListHeight ?? 0))
      }, 50)

      getHeaderHeight()
      window.addEventListener('resize', getHeaderHeight, { passive: true })

      return () => {
        window.removeEventListener('resize', getHeaderHeight)
      }
    }
  }, [isScrollingDown])

  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()
  const filters = useMemo(() => {
    const filteredList = filterListOnExp(
      excludeDepartmentFilter(serverFilters, collectionSlug),
      hideDeptAndCategoryFilter,
    ).filter(item => item.values.length > 1)

    return filteredList
  }, [serverFilters, hideDeptAndCategoryFilter, collectionSlug])

  // Enhanced drawer control with direct filter access
  const openDrawerWithFilter = (filterName: string) => {
    // Then open drawer with that filter expanded
    setIsDrawerOpen(true)
    setCollapseActiveKeys([filterName])
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: filterName,
    })
  }

  // Reset handler for when the main filter button is clicked
  const handleMainFilterClick = () => {
    // Keep all filters collapsed by default
    setCollapseActiveKeys([])
    setIsDrawerOpen(true)
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: FILTER_KEY_GLOBAL,
    })
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    setCollapseActiveKeys([])
  }

  // Add these sort drawer functions
  const openSortDrawer = () => {
    setIsSortDrawerOpen(true)
    trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
  }

  const closeSortDrawer = () => {
    setIsSortDrawerOpen(false)
  }

  // Add a collapseChange handler
  const handleCollapseChange = (activeKeys: string[]) => {
    setCollapseActiveKeys(activeKeys)
  }

  return {
    isDrawerOpen,
    isSortDrawerOpen,
    collapseActiveKeys,
    headerHeight,
    isScrollingDown,
    filters,
    handleCollapseChange,
    handleMainFilterClick,
    closeDrawer,
    openDrawerWithFilter,
    openSortDrawer,
    closeSortDrawer,
  }
}

export default useMobileFilters
